import { backofficeApi } from '../api';
import {
  getProspectResponse,
  getProspectsParams,
  getTerritoryManagersResponse,
  postProspectSubstateResponse,
  prospect,
  prospectsResponse,
  prospectSubstates,
  updateProspectSubstateParams,
} from './types';

export const getProspects = async (data?: getProspectsParams) => {
  const searchParams = new URLSearchParams(typeof data === 'string' ? data : (data as Record<string, string>) || {});
  searchParams.delete('page');
  if (!searchParams.get('offset')) {
    searchParams.set('offset', '0');
  }
  if (!searchParams.get('limit')) {
    searchParams.set('limit', '10');
  }
  if (!searchParams.get('sort') && !searchParams.get('sortDirection') && !searchParams.get('sortIndex')) {
    searchParams.append('sort', 'STATE,ASC');
    searchParams.append('sort', 'GENERATED_AT,ASC');
  }
  const cleanedParams = searchParams.toString();
  return backofficeApi<prospectsResponse>({
    url: `prospects?${cleanedParams}`,
  });
};

export const getProspect = async (id: string) =>
  backofficeApi<getProspectResponse>({
    url: `prospects/${id}`,
  });

export const updateProspectSubstate = async ({ id, headquartersCode, to }: updateProspectSubstateParams) =>
  backofficeApi<getProspectResponse>({
    method: 'POST',
    url: `prospects/${id}:move`,
    data: { headquartersCode, to },
  });

export const postProspectSubstate = async ({
  id,
  substate,
  headquartersCode,
}: {
  id: prospect['id'];
  substate: prospectSubstates;
  headquartersCode?: prospect['headquartersCode'];
}) =>
  backofficeApi<postProspectSubstateResponse>({
    url: `prospects/${id}:move`,
    method: 'POST',
    data: { to: substate, headquartersCode: substate === 'COMPLETED' ? Number(headquartersCode) : undefined },
    parseJson: true,
  });

export const downloadBackofficeFile = async ({ prospectId, documentId }: { prospectId: string; documentId: string }) =>
  backofficeApi<Blob>({
    url: `prospects/${prospectId}/documents/${documentId}:content`,
    responseType: 'blob',
  });

export const changeBackofficeFileStatus = async ({
  prospectId,
  documentId,
  status,
  comment,
}: {
  prospectId: string;
  documentId: string;
  status: string;
  comment: string;
}) =>
  backofficeApi<getProspectResponse>({
    url: `prospects/${prospectId}/documents/${documentId}:${status}`,
    method: 'POST',
    data: { comment },
  });

export const patchBackofficeFile = async ({
  prospectId,
  documentId,
  comment,
}: {
  prospectId: string;
  documentId: string;
  comment: string;
}) =>
  backofficeApi<getProspectResponse>({
    url: `prospects/${prospectId}/documents/${documentId}`,
    method: 'PATCH',
    data: { comment },
  });

export const getTerritoryManagers = async () =>
  backofficeApi<getTerritoryManagersResponse>({
    url: `territory-managers`,
  });

export const patchProspect = async ({
  prospectId,
  territoryManagerId,
  legalRepresentativeEmail,
}: {
  prospectId: string;
  territoryManagerId?: number;
  legalRepresentativeEmail?: string;
}) => {
  const data = {
    ...(territoryManagerId !== undefined ? { territoryManagerId } : {}),
    ...(legalRepresentativeEmail !== undefined ? { legalRepresentativeEmail } : {}),
  };
  return backofficeApi<getProspectResponse>({
    url: `prospects/${prospectId}`,
    method: 'PATCH',
    data,
  });
};

export const rejectProspect = async ({ id }: { id: prospect['id'] }) =>
  backofficeApi<getProspectResponse>({
    url: `prospects/${id}:reject`,
    method: 'POST',
  });

export const deleteFile = async ({ prospectId, documentId }: { prospectId: string; documentId: string }) =>
  backofficeApi<void>({
    url: `prospects/${prospectId}/documents/${documentId}`,
    method: 'DELETE',
  });

export const downloadProspectsViaEmail = async ({ params }: { params: string }) =>
  backofficeApi<void>({
    url: `prospects:export?${params}`,
  });
